import { useCachedTellescopeQueries } from "../use-cached-tellescope-queries";
import { useMemo } from "react";
import type {
  ModelName,
  QueryDataForMethod,
  QueryMethodForModel,
  TellescopeQuery,
  TellescopeQueryKey,
} from "../../types";
import { extractMethodFromQueryKey } from "../../keys";

interface TaggedRecord {
  tags?: string[];
}

const addTags = (acc: Set<string>, record: TaggedRecord | undefined) => record?.tags?.forEach((tag) => acc.add(tag));

/**
 * Shared tag extraction for any model whose getOne/getSome/getByIds responses
 * carry tags directly on each record. The runtime shapes are identical across
 * these models; only the (erased) cast types differ, so a single generic
 * implementation covers all of them.
 */
const extractStandardTags = <Model extends ModelName>(
  queries: TellescopeQuery<Model, QueryMethodForModel<Model>>[]
): string[] =>
  Array.from(
    queries.reduce<Set<string>>((acc, curr) => {
      const method = extractMethodFromQueryKey(curr.queryKey as TellescopeQueryKey<Model, QueryMethodForModel<Model>>);
      switch (method) {
        case "getOne": {
          addTags(acc, curr.state.data as TaggedRecord | undefined);
          break;
        }
        case "getSome": {
          (curr.state.data as { pages?: TaggedRecord[][] } | undefined)?.pages?.forEach((page) =>
            page.forEach((record) => addTags(acc, record))
          );
          break;
        }
        case "getByIds": {
          (curr.state.data as { matches?: TaggedRecord[] } | undefined)?.matches?.forEach((match) =>
            addTags(acc, match)
          );
          break;
        }
      }
      return acc;
    }, new Set<string>())
  );

const TAG_EXTRACTION_FUNCTIONS = {
  sms_messages: extractStandardTags,
  phone_calls: extractStandardTags,
  forms: extractStandardTags,
  tickets: extractStandardTags,
  chat_rooms: extractStandardTags,
  emails: extractStandardTags,
  endusers: extractStandardTags,
  appointment_locations: extractStandardTags,
  templates: extractStandardTags,
  calendar_event_templates: extractStandardTags,
  appointment_booking_pages: extractStandardTags,
  users: extractStandardTags,
  inbox_threads: (queries) =>
    Array.from(
      queries.reduce<Set<string>>((acc, curr) => {
        const method = extractMethodFromQueryKey(
          curr.queryKey as TellescopeQueryKey<"inbox_threads", QueryMethodForModel<"inbox_threads">>
        );
        switch (method) {
          case "load_threads": {
            const typedData = curr.state.data as unknown as
              | QueryDataForMethod<"inbox_threads", "load_threads">
              | undefined;
            typedData?.pages.forEach((page) =>
              page.threads.forEach((thread) => thread.tags?.forEach((tag) => acc.add(tag)))
            );
            break;
          }
        }
        return acc;
      }, new Set<string>())
    ),
} as const satisfies Partial<{
  [Model in ModelName]: (queries: TellescopeQuery<Model, QueryMethodForModel<Model>>[]) => string[];
}>;

export type ModelsWithCachedTags = keyof typeof TAG_EXTRACTION_FUNCTIONS;

export interface UseCachedTagsArgs<T extends ModelsWithCachedTags> {
  model: T;
}

/**
 * Retrieves all currently cached tags from a given model type
 *
 * This hook uses the reactive useCachedTellescopeQueries hook to automatically
 * update when the query cache changes, eliminating the need for manual polling.
 *
 * @param model - The model type from which to retrieve all cached tags
 *
 * @returns An object containing:
 *   - tags: the tags retrieved from the cache (automatically updates on cache changes)
 *   - recheckTags: a function to manually trigger cache invalidation and refresh
 */
export const useCachedTags = <T extends ModelsWithCachedTags>({ model }: UseCachedTagsArgs<T>) => {
  const select = useMemo<(queries: TellescopeQuery<T, QueryMethodForModel<T>>[]) => string[]>(
    () =>
      TAG_EXTRACTION_FUNCTIONS[model] as unknown as (queries: TellescopeQuery<T, QueryMethodForModel<T>>[]) => string[],
    [model]
  );
  return useCachedTellescopeQueries<T, QueryMethodForModel<T>, string[]>({
    model,
    select,
  });
};
